import { flatMapDeep } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Gantt as GanttAtom } from 'components/atoms/gantt';
import { PageLoading } from 'components/atoms/loading';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { useTransformTabularData } from 'data/templates/projects';
import { queueMacrotask } from 'utils/macrotask';
import { ProjectTemplateToolBar } from '../commons';
// eslint-disable-next-line import/no-internal-modules
import { useTableActions } from '../plan/table/actions';
import { useTemplateGanttDataConfig } from './config/use-data-config';
import { useTemplateGanttEventsConfig } from './config/use-events-config';
import { useTemplateGanttStaticConfig } from './config/use-static-config';
import { useGanttRef } from './use-gantt-ref';

const Gantt = styled(GanttAtom).attrs((props) => ({
  splitter: false,
  numberOfcells: props.numberOfCells,
}))`
  margin-left: var(--layout-02);
  height: calc(100% - 48px) !important; /* subtracting height of switcher bar */

  .b-sch-header-row-0 {
    display: none;
  }

  .b-date-cell,
  .b-number-cell {
    padding: 0 calc(var(--layout-02) + var(--spacing-01));
  }
  /**
    Patch to show column lines on weekend as column lines on other days,
    gantt is applying the following class to column lines on weekends
    applying a different border for those lines.
    TODO: to tweak the config for consistent column lines
   */
  .b-column-line-major {
    border-color: #f6f6f6;
  }

  .b-grid-panel-body > .b-grid-body-container > .b-grid-vertical-scroller {
    height: ${({ numberOfcells }) =>
      `calc(calc(${numberOfcells} * 41px) + var(--spacing-05)) !important`};
    padding-bottom: var(--spacing-05);
    min-height: 0 !important;
  }
`;

export const ProjectTemplateGanttPage: FC = () => {
  const { mode, expandCollapse } = useTableActions();
  const [showLoader, setShowLoader] = useState(true);
  const { ganttRef, setGanttRef, collapseAll, expandAll } =
    useGanttRef(showLoader);

  const data = useTransformTabularData();

  const staticConfig = useTemplateGanttStaticConfig({ ganttRef });
  const dataConfig = useTemplateGanttDataConfig();
  const eventsConfig = useTemplateGanttEventsConfig({
    ganttRef,
  });

  /**
   * Forcefully showing loader for zero timeout
   * for faster tab switching in template detail.
   * else, user stays on other tab until gantt loads.
   */
  useEffect(() => {
    queueMacrotask(() => {
      setShowLoader(false);
    });
  }, []);

  useEffect(() => {
    switch (mode) {
      case 'collapse':
        collapseAll();
        break;
      case 'expand':
      default:
        expandAll();
        break;
    }
  }, [collapseAll, expandAll, mode]);

  const numberOfCells = useMemo(() => {
    const flatten = (item) => {
      return [item, flatMapDeep(item.children, flatten)];
    };

    const result = flatMapDeep(dataConfig.project.eventsData, flatten);

    return result.length;
  }, [dataConfig.project.eventsData]);

  return (
    <>
      <ProjectTemplateToolBar
        data={data}
        mode={mode}
        expandCollapse={expandCollapse}
      />
      <ErrorBoundary fallback={() => <>Something went wrong</>}>
        {!showLoader ? (
          <Gantt
            {...staticConfig}
            {...dataConfig}
            {...eventsConfig}
            ganttRef={setGanttRef}
            numberOfCells={numberOfCells}
          />
        ) : (
          <PageLoading css />
        )}
      </ErrorBoundary>
    </>
  );
};
